<template>
  <div>
    <VBtn
      v-if="showActivateButton"
      color="success"
      class="mr-4 mb-2"
      :loading="activateButtonLoading"
      :disabled="isDisabledButton"
      @click="activate"
    >
      {{ $t('publishVacancy') }}
    </VBtn>
    <VBtn
      v-if="showRestoreButton"
      color="success"
      class="mr-4 mb-2"
      :loading="restoreButtonLoading"
      :disabled="isDisabledButton"
      @click="restore"
    >
      {{ $t('restore') }}
    </VBtn>
    <VBtn
      v-if="showRemoveDraftButton"
      color="error"
      class="mr-4 mb-2"
      :loading="removeDraftButtonLoading"
      :disabled="isDisabledButton"
      @click="removeVacancy"
    >
      {{ $t('removeVacancy') }}
    </VBtn>
    <VBtn
      v-if="showToArchiveButton"
      color="success"
      class="mr-4 mb-2"
      :loading="toArchiveButtonLoading"
      :disabled="isDisabledButton"
      @click="toArchive"
    >
      {{ $t('toArchive') }}
    </VBtn>
    <VBtn
      v-if="showMakeCopyButton"
      color="success"
      class="mr-4 mb-2"
      :loading="makeCopyButtonLoading"
      :disabled="isDisabledButton"
      @click="makeCopy"
    >
      {{ $t('createCopy') }}
    </VBtn>
    <VBtn
      v-if="true"
      color="success"
      class="mr-4 mb-2"
      :loading="redactingTestButtonLoading"
      :disabled="isDisabledButton"
      @click="redactingTheTest"
    >
      {{ $t('editTest') }}
    </VBtn>
    <VDialog
      v-model="dialogExpiredTariff"
      persistent
      max-width="600"
    >
      <DialogExpiredTariff @close="dialogExpiredTariff = false" />
    </VDialog>
  </div>
</template>

<script>
import ValidationMixin from '../../mixins/ValidationMixin';
import useSubscription from '@/compositions/accounting/useSubscription';
import { vacancyStatus } from '../../enums';
import DialogExpiredTariff from '../DialogExpiredTariff.vue';
import Vacancy from '../../orm/models/Vacancy/Vacancy';
import { response } from '../../orm/functions';
import { safeGet } from '../../functions';

export default {
  name: 'VacancyCardButtonsPanel',
  components: { DialogExpiredTariff },
  mixins: [ValidationMixin],
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    limitedVacancies: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  setup() {
    const { isExpired } = useSubscription();
    return { isExpired };
  },
  data() {
    return {
      activateButtonLoading: false,
      restoreButtonLoading: false,
      removeDraftButtonLoading: false,
      toArchiveButtonLoading: false,
      makeCopyButtonLoading: false,
      redactingTestButtonLoading: false,
      dialogExpiredTariff: false,
    };
  },
  computed: {
    interviewerId() {
      return safeGet(this.vacancy, ['script', 'interviewer', 'id']);
    },
    vacancyCompleted() {
      return !!this.interviewerId;
    },
    showActivateButton() {
      return this.vacancy.status === vacancyStatus.DRAFT && this.vacancyCompleted;
    },
    showRestoreButton() {
      return this.vacancy.status === vacancyStatus.ARCHIVE;
    },
    showRemoveDraftButton() {
      return this.vacancy.status === vacancyStatus.DRAFT;
    },
    showToArchiveButton() {
      return this.vacancy.status === vacancyStatus.ACTIVE;
    },
    showMakeCopyButton() {
      return this.vacancy.status === vacancyStatus.ACTIVE
        || this.vacancy.status === vacancyStatus.ARCHIVE;
    },
    isDisabledButton() {
      return this.vacancy.isAiInterviewCompiling
      || this.vacancy.isAiInterviewRecompiling
      || this.vacancy.aiQuestionsGenerating;
    },
  },
  methods: {
    async activate() {
      try {
        await this.withLoading(async () => {
          await Vacancy.api().activate(this.vacancy.id);
          this.$toast.success(this.$t('vacancyPublished'));
        }, 'activateButtonLoading');
      } catch (e) {
        this.$toast.error(e.message);
      }
    },
    async toArchive() {
      try {
        await this.withLoading(async () => {
          await Vacancy.api().archive(this.vacancy.id);
          this.$toast.success(this.$t('vacancyArchived'));
        }, 'toArchiveButtonLoading');
      } catch (e) {
        this.$toast.error(e.message);
      }
    },
    async restore() {
      try {
        await this.withLoading(async () => {
          if (this.limitedVacancies) {
            this.$emit('openDialogMaximumVacancies', 'restore');
            return;
          }
          await Vacancy.api().restore(this.vacancy.id);
          this.$toast.success(this.$t('vacancyRestored'));
        }, 'restoreButtonLoading');
      } catch (e) {
        this.$toast.error(e.message);
      }
    },
    async removeVacancy() {
      try {
        await this.withLoading(async () => {
          await Vacancy.api().destroy(this.vacancy.id);
        }, 'removeDraftButtonLoading');
        await this.$router.push({ name: 'vacancies' });
        await Vacancy.delete(this.vacancy.id);
      } catch (e) {
        this.$toast.error(e.message);
      }
    },
    async makeCopy() {
      if (this.isExpired) {
        this.dialogExpiredTariff = true;
        return;
      }
      try {
        await this.withLoading(async () => {
          if (this.limitedVacancies) {
            this.$emit('openDialogMaximumVacancies', 'clone');
            return;
          }
          const data = await response(Vacancy.api().clone(this.vacancy.id));
          this.$toast.success(this.$t('vacancyCopied'));
          if (this.vacancy.isAiInterviewCompiled) {
            await this.$router.push({ name: 'vacancies' });
            return;
          }
          await this.$router.push({ name: 'vacancy', params: { id: data.id } });
        }, 'makeCopyButtonLoading');
      } catch (e) {
        this.$toast.error(e.message);
      }
    },
    async redactingTheTest() {
      try {
        await this.withLoading(async () => {
          await this.$router.push({
            name: 'vacancy-testing-edit',
            params: { vacancyId: this.vacancy.id },
          });
        }, 'redactingTestButtonLoading');
      } catch (e) {
        this.$toast.error(e.message);
      }
    },
  },
};
</script>

<i18n locale="en" lang="yaml">
  publishVacancy: Publish job
  restore: Restore
  removeVacancy: Remove draft
  toArchive: Archive
  createCopy: Copy
  vacancyPublished: Job published
  vacancyArchived: Job archived
  vacancyRestored: Job restored
  vacancyCopied: Job copied
  editTest: Edit test
</i18n>

<i18n locale="ru" lang="yaml">
  publishVacancy: Разместить вакансию
  restore: Восстановить
  removeVacancy: Удалить черновик
  toArchive: В архив
  createCopy: Создать копию
  vacancyPublished: Вакансия запущена
  vacancyArchived: Вакансия перемещена в архив
  vacancyRestored: Вакансия восстановлена
  vacancyCopied: Вакансия скопирована
  editTest: Редактировать тест
</i18n>
